import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Icon from "../common/icons";
import { fetchUpdates, markAllRead, updatesChanged } from "./updatesStore";
import "./studentUpdates.css";

/**
 * The student's Updates: what changed for them (a talk moved or cancelled, results shared),
 * newest first, one button each. One way only: there is nothing to reply to here.
 * A student dialog (yellow bar from styles/modals.css); a sheet from the bottom on phones.
 */

export const PHONE_QUERY = "(max-width: 767.98px)";

const KIND_ICONS = {
  reminder: "calendar",
  session_moved: "calendar-check",
  session_cancelled: "calendar-x",
  session_reassigned: "user",
  waitlist_opening: "calendar-plus",
  results_ready: "bar-chart",
  session_booked: "calendar-event",
  session_place: "map-pin",
  session_kept: "calendar-check",
  tests_assigned: "clipboard-list",
};

const dayFormat = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });

export const whenLabel = (iso, now = new Date()) => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const minutes = Math.round((now - at) / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24 && at.getDate() === now.getDate()) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (at.toDateString() === yesterday.toDateString()) return "Yesterday";
  return dayFormat.format(at);
};

const usePhone = () => {
  const [phone, setPhone] = useState(() => window.matchMedia?.(PHONE_QUERY).matches ?? false);
  useEffect(() => {
    const query = window.matchMedia?.(PHONE_QUERY);
    if (!query) return undefined;
    const onChange = () => setPhone(query.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);
  return phone;
};

const UpdateRow = ({ item, index, onOpen }) => (
  <li className={`ud-item${item.read ? "" : " is-new"}`} style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}>
    <span className="ud-tile" aria-hidden="true">
      <Icon name={KIND_ICONS[item.kind] || "bell"} size={22} />
    </span>
    <div className="ud-item-main">
      <div className="ud-item-top">
        <b className="ud-item-title">{item.title}</b>
        {!item.read && <span className="ud-new">New</span>}
      </div>
      {item.body && <p className="ud-item-body">{item.body}</p>}
      <div className="ud-item-foot">
        <span className="ud-when">{whenLabel(item.created_at)}</span>
        {item.url && item.action_label && (
          <button type="button" className="am-btn is-primary is-small ud-action" onClick={() => onOpen(item)}>
            {item.action_label}
            <Icon name="arrow-right" size={16} />
          </button>
        )}
      </div>
    </div>
  </li>
);

export default function UpdatesSheet({ show, onClose }) {
  const phone = usePhone();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    return fetchUpdates()
      .then((data) => {
        const rows = data.updates || [];
        setItems(rows);
        // Seen now: the dot goes; the rows keep their "New" tag while the sheet is open
        if (rows.some((row) => !row.read)) {
          markAllRead()
            .then((answer) => updatesChanged({ unread: Number(answer?.unread) || 0 }))
            .catch(() => {});
        }
      })
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    if (!show) return;
    setItems(null);
    load();
  }, [show, load]);

  const open = (item) => {
    onClose();
    navigate(item.url);
  };

  let body;
  if (failed) {
    body = (
      <div className="ud-empty" role="alert">
        <span className="ud-empty-tile" aria-hidden="true"><Icon name="wifi-off" size={30} /></span>
        <b className="ud-empty-title">Couldn’t load your updates</b>
        <p className="ud-empty-text">Check your connection, then try again.</p>
        <button type="button" className="am-btn is-primary" onClick={load}>Try again</button>
      </div>
    );
  } else if (items === null) {
    body = (
      <ul className="ud-list" aria-busy="true" aria-label="Loading your updates">
        {[0, 1, 2].map((key) => <li key={key} className="ud-item is-loading"><span className="ud-tile" /><div className="ud-item-main"><span className="ud-bar" /><span className="ud-bar is-short" /></div></li>)}
      </ul>
    );
  } else if (!items.length) {
    body = (
      <div className="ud-empty">
        <span className="ud-empty-tile" aria-hidden="true"><Icon name="bell" size={30} /></span>
        <b className="ud-empty-title">No updates yet</b>
        <p className="ud-empty-text">
          When the guidance office moves or cancels a talk, or shares your results, you’ll see it here.
        </p>
      </div>
    );
  } else {
    body = (
      <ul className="ud-list">
        {items.map((item, index) => <UpdateRow key={item.id} item={item} index={index} onOpen={open} />)}
      </ul>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered={!phone}
      scrollable={!phone}
      dialogClassName={phone ? "ud-sheet" : "ud-dialog"}
      contentClassName="ud-card"
      aria-labelledby="ud-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="ud-title">
          <Icon name="bell" size={22} />
          Updates
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{body}</Modal.Body>
    </Modal>
  );
}
